import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import {ConnectedProps} from 'react-redux'
import * as Yup from 'yup'

import config from '../../config'

import * as coreActions from '../../actions/core'
import * as modalActions from '../../actions/modal'
import * as orderActions from '../../actions/order'
import {State} from '../../reducers'
import {getInitialStatusFilter} from '../../reducers/order'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as localStorage from '../../../../commons/src/services/local-storage'
import {captureException} from '../../../../commons/src/services/logging'
import {connect} from '../../../../commons/src/services/loop'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import useSelector from '../../hooks/use-selector'
import {formatCurrency} from '../../libs/formatter'
import {anyPermissionExceptOperator} from '../../libs/user'
import * as providerApi from '../../services/api/provider'
import * as providerEmailApi from '../../services/api/provider-email'
import {toNumberValue, toStringValue, toTouchedFormData} from '../utils/form'
import {isPermitted} from '../utils/routing'

import {Permission, Provider} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import lockIcon from '../../../../commons/src/assets/icons/lock.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox'
import LinkButton from '../../../../commons/src/components/link-button'
import Link from '../../../../commons/src/components/link/Link'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import {orderStatusTypeToContentMap} from '../../components/order-status-badge/OrderStatusBadge'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'

import {OrderStatusType} from '../../order-types'
import RequireAuthPartial from '../require-auth-partial'

export type SingleFormData = {
  minimumProductionPrice: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const validationSchema = {
  minimumProductionPrice: Yup.number().min(1).label('Minimum production price'),
}

const initialValues = {
  minimumProductionPrice: '',
}

const providerToFormData = (provider: Provider) => ({
  minimumProductionPrice: provider.minimumProductionPrice
    ? toStringValue(provider.minimumProductionPrice)
    : '',
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  minimumProductionPrice: toNumberValue,
}

const formDataToPartialRequest = (formData: Partial<SingleFormData>) => {
  return {
    minimumProductionPrice: formDataTransforms.minimumProductionPrice(
      formData.minimumProductionPrice
    ),
  }
}

const mapStateToProps = (state: State) => ({
  user: state.auth.user,
})

const mapDispatchToProps = {
  updateProvider: coreActions.updateProvider,
  openChangePasswordModal: modalActions.openChangePasswordModal,
  openEditProviderEmailsModal: modalActions.openEditProviderEmailsModal,
  updateStatusFilter: orderActions.updateStatusFilter,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const SettingsGeneralPartial = ({
  user,
  updateProvider,
  openChangePasswordModal,
  openEditProviderEmailsModal,
  updateStatusFilter,
}: Props) => {
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const provider = useSelector(state => state.core.provider)
  invariant(provider, 'Provider missing.')

  const [defaultOrderStatusFiltersSubmitted, setDefaultOrderStatusFiltersSubmitted] =
    React.useState(false)
  const [defaultOrderStatusFilters, setDefaultOrderStatusFilters] = React.useState(
    localStorage.getItem(config.localStorageKeys.defaultStatusFilters) ||
      config.defaultStatusFilters
  )

  const {
    isLoading: isLoadingProviderContactEmails,
    data: providerContactEmails = [],
    refresh: receiveProviderContactEmails,
    error: providerContactEmailError,
  } = useAsync(() => providerEmailApi.getByProvider(user?.permission, provider.id), [], {
    isEnabled: !!user && isPermitted(user.permission, [Permission.PROVIDER]),
    onError: error => {
      captureException(error)
    },
  })

  const minProdFormik = useFormik<SingleFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...providerToFormData(provider),
    },
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerRequest = formDataToPartialRequest(touchedValues)

      try {
        const nextProvider = await providerApi.patch(providerRequest, provider.id)

        setIsSubmitted(true)
        updateProvider(nextProvider)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const renderDefaultOrderStatusFilterSettings = () => {
    const orderStatusOptions = omit(orderStatusTypeToContentMap, [OrderStatusType.UNKNOWN])
    const localStorageAvailable = localStorage.hasLocalStorage

    return (
      <Section>
        <Stack>
          <Headline label="Default order status filter" classNames={['u-no-margin-bottom']} />
          <Stack space="60">
            <Paragraph classNames={['u-no-margin-bottom']}>
              You can set your preferred default status filter values for the order table here.
            </Paragraph>
            <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
              Note: Your preferences are stored in your browser&apos;s local storage and are not
              tied to your account.
            </Paragraph>
          </Stack>
          {!localStorageAvailable && (
            <Paragraph warning classNames={['u-no-margin-bottom']}>
              This feature is disabled because we cannot access the browsers local storage.
            </Paragraph>
          )}
          <div>
            {Object.keys(orderStatusOptions).map((key: string) => {
              const type = key as OrderStatusType
              const option = orderStatusTypeToContentMap[type]
              return (
                <LabeledCheckbox
                  key={option.type}
                  label={option.label}
                  checked={defaultOrderStatusFilters.includes(type)}
                  onChange={value => {
                    setDefaultOrderStatusFiltersSubmitted(false)
                    if (value) {
                      setDefaultOrderStatusFilters([...defaultOrderStatusFilters, type])
                    } else {
                      setDefaultOrderStatusFilters(
                        defaultOrderStatusFilters.filter(
                          (filterValue: OrderStatusType) => filterValue !== type
                        )
                      )
                    }
                  }}
                  compact
                  disabled={!localStorageAvailable}
                />
              )
            })}
          </div>
          <Button
            classNames={['u-margin-top-base']}
            disabled={
              !localStorageAvailable ||
              isEqual(
                localStorage.getItem(config.localStorageKeys.defaultStatusFilters),
                defaultOrderStatusFilters
              )
            }
            icon={defaultOrderStatusFiltersSubmitted ? <Icon source={checkedIcon} /> : undefined}
            onClick={preventDoubleClick(() => {
              localStorage.setItem(
                config.localStorageKeys.defaultStatusFilters,
                defaultOrderStatusFilters
              )
              setDefaultOrderStatusFiltersSubmitted(true)
              // reset status filters in state
              updateStatusFilter(getInitialStatusFilter())
            })}
          >
            Save
          </Button>
        </Stack>
      </Section>
    )
  }

  return (
    <>
      <Section>
        <Headline label="Your Login Address" />
        <InputField
          disabled
          icon={lockIcon}
          value={user?.attributes?.email || ''}
          label="E-Mail"
          classNames={['u-margin-bottom-l']}
        />
        <Headline label="Your Password" />
        <InputField
          disabled
          type="password"
          value="xxxxxxxxxxx"
          label="Password"
          classNames={['u-margin-bottom']}
        />
        <Button hierarchy="secondary" onClick={() => openChangePasswordModal()}>
          Change password
        </Button>
      </Section>
      <RequireAuthPartial requiredPermissions={[Permission.PROVIDER]}>
        <Section>
          <SplitLayout
            left={<Headline label="Your Contact Email Addresses" />}
            right={
              <LinkButton
                size="200"
                icon={<Icon source={editIcon} title="Edit" />}
                disabled={isLoadingProviderContactEmails || !!providerContactEmailError}
                onClick={() =>
                  openEditProviderEmailsModal(providerContactEmails, receiveProviderContactEmails)
                }
              />
            }
          />
          <Table
            empty={
              providerContactEmailError ? (
                <Paragraph warning>
                  We were not able to load your contact emails.{' '}
                  <LinkButton
                    disabled={isLoadingProviderContactEmails}
                    onClick={receiveProviderContactEmails}
                  >
                    Please try again!
                  </LinkButton>
                </Paragraph>
              ) : (
                'No contact emails yet'
              )
            }
            isLoading={isLoadingProviderContactEmails}
            header={
              <TableRow>
                <TableHeaderCell width={4}>Email</TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    openOnHover
                    timeout={500}
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.ordersDescription}</Tooltip>
                    }
                  >
                    <Link>Orders</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    openOnHover
                    timeout={500}
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.invoiceDescription}</Tooltip>
                    }
                  >
                    <Link>Invoices</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    openOnHover
                    timeout={500}
                    tooltip={
                      <Tooltip appearance="dark">
                        {config.providerEmail.messagesDescription}
                      </Tooltip>
                    }
                  >
                    <Link>Messages</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    openOnHover
                    timeout={500}
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.newsDescription}</Tooltip>
                    }
                  >
                    <Link>News</Link>
                  </TooltipHandler>
                </TableHeaderCell>
              </TableRow>
            }
          >
            {providerContactEmails.map(contact => (
              <TableRow key={contact.id}>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.orders ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.invoices ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.messages ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.news ? <Icon source={checkedIcon} /> : '-'}</TableCell>
              </TableRow>
            ))}
          </Table>
        </Section>
      </RequireAuthPartial>
      <RequireAuthPartial requiredPermissions={anyPermissionExceptOperator}>
        <Section>
          <Headline label="Minimum production cost" />
          <Paragraph>Set a minimum production cost that you will receive for an order.</Paragraph>
          <form onSubmit={minProdFormik.handleSubmit}>
            {minProdFormik.errors.formikGeneral && (
              <Paragraph error>{minProdFormik.errors.formikGeneral}</Paragraph>
            )}
            <FieldNote
              type="error"
              note={minProdFormik.submitCount > 0 && minProdFormik.errors.minimumProductionPrice}
              classNames={['u-margin-bottom']}
            >
              <NumberInputField
                allowDecimals
                allowOnly2Decimals
                name="minimumProductionPrice"
                label="Min production cost"
                value={minProdFormik.values.minimumProductionPrice}
                error={
                  minProdFormik.submitCount > 0 && !!minProdFormik.errors.minimumProductionPrice
                }
                suffix={formatCurrency(provider.currency)}
                onChange={(value, name) => {
                  minProdFormik.setFieldValue(name as string, value)
                  if (isSubmitted) {
                    setIsSubmitted(false)
                  }
                }}
                onBlur={minProdFormik.handleBlur}
              />
            </FieldNote>
          </form>
          <Button
            classNames={['u-margin-top-base']}
            disabled={minProdFormik.isSubmitting || isSubmitted || !minProdFormik.dirty}
            icon={
              isSubmitted ? (
                <Icon source={checkedIcon} />
              ) : minProdFormik.isSubmitting &&
                minProdFormik.submitCount > 0 &&
                !isEmpty(minProdFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => minProdFormik.handleSubmit())}
          >
            {isSubmitted ? 'Saved' : 'Save'}
          </Button>
        </Section>
      </RequireAuthPartial>
      {renderDefaultOrderStatusFilterSettings()}
    </>
  )
}

export default connector(SettingsGeneralPartial)
